import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  BusinessRole,
  ResourceCategory,
  ResourceType,
  Trade,
} from "@/modules/hub/features/ResourceLibrary/api/getResourceLibraryMetadata/types";
import { resourceLibraryAlgoliaService } from "@/modules/hub/features/ResourceLibrary/services/resourceLibraryAlgoliaService";

interface ResourceLibraryMetadataState {
  metadata: {
    trades: Trade[];
    resourceTypes: ResourceType[];
    resourceCategories: ResourceCategory[];
    businessRoles: BusinessRole[];
  } | null;
  loadingGet: boolean;
  errorGet: string | null;
}

const initialState: ResourceLibraryMetadataState = {
  metadata: null,
  loadingGet: false,
  errorGet: null,
};

const resourceLibraryMetadataSlice = createSlice({
  name: "resourceLibraryMetadata",
  initialState,
  reducers: {
    setMetadata: (
      state,
      action: PayloadAction<{
        trades: Trade[];
        businessRoles: BusinessRole[];
        resourceTypes: ResourceType[];
        resourceCategories: ResourceCategory[];
      }>,
    ) => {
      state.metadata = action.payload;
    },
    setLoadingGet: (state, action: PayloadAction<boolean>) => {
      state.loadingGet = action.payload;
    },
    setErrorGet: (state, action: PayloadAction<string | null>) => {
      state.errorGet = action.payload;
    },
    resetFilters: () => initialState,
  },
});

export const { setMetadata, setLoadingGet, setErrorGet, resetFilters } =
  resourceLibraryMetadataSlice.actions;

export const getResourceLibraryMetadataAction =
  (): AppThunk => async (dispatch: AppDispatch) => {
    dispatch(setLoadingGet(true));
    dispatch(setErrorGet(null));
    try {
      // Get metadata from Algolia facets instead of backend API
      const algoliaMetadata = await resourceLibraryAlgoliaService.getMetadata();

      dispatch(
        setMetadata({
          trades: algoliaMetadata.trades,
          businessRoles: algoliaMetadata.businessRoles,
          resourceTypes: algoliaMetadata.resourceTypes,
          resourceCategories: algoliaMetadata.categories,
        }),
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch metadata from Algolia.";
      dispatch(setErrorGet(message));
    } finally {
      dispatch(setLoadingGet(false));
    }
  };

export default resourceLibraryMetadataSlice.reducer;
